import { hasPermission } from '@serviceportal/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useMemo, type ReactNode } from 'react';
import { api, ApiError, setCsrfToken } from './api';
import { applyBranding } from './branding';
import type { Me } from './types';

interface AuthValue {
  me: Me | null;
  loading: boolean;
  can: (permission: string) => boolean;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthValue | null>(null);

export const ME_KEY = ['auth', 'me'] as const;

async function fetchMe(): Promise<Me | null> {
  try {
    const me = await api<Me>('/auth/me');
    setCsrfToken(me.csrfToken);
    applyBranding(me.tenant);
    return me;
  } catch (e) {
    if (e instanceof ApiError && e.status === 401) {
      setCsrfToken(null);
      return null;
    }
    throw e;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ME_KEY, queryFn: fetchMe, staleTime: 60_000, retry: 1 });

  const refresh = useCallback(async () => {
    await qc.invalidateQueries({ queryKey: ME_KEY });
  }, [qc]);

  const logout = useCallback(async () => {
    await api('/auth/logout', { method: 'POST' }).catch(() => undefined);
    setCsrfToken(null);
    // Erst den Anmeldestatus setzen, dann die übrigen Daten verwerfen. qc.clear() würde
    // auch die Anmeldeabfrage samt Beobachtern entfernen – die Oberfläche bliebe angemeldet.
    qc.setQueryData(ME_KEY, null);
    qc.removeQueries({ predicate: (q) => q.queryKey[0] !== ME_KEY[0] });
  }, [qc]);

  const value = useMemo<AuthValue>(
    () => ({
      me: data ?? null,
      loading: isLoading,
      can: (p) => Boolean(data && hasPermission(data.permissions, p)),
      refresh,
      logout,
    }),
    [data, isLoading, refresh, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth außerhalb von AuthProvider');
  return ctx;
}
